import { Component, OnInit, AfterViewInit, Input, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import {
  Company,
  FileItem,
  LineItem,
  Product,
  PurchaseOrderDto,
  PurchaseOrderSubscriberModel,
  User,
} from '@app/models';
import { Shipment } from '@models/shipment.model';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LocalStorage } from 'ngx-webstorage';
import { CompanyService, AppPurchaseOrderService, Toaster } from '@app/services';
import { ShipmentService } from '@services/shipment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import * as moment from 'moment';
import Swal from 'sweetalert2';
import { CreateCompanyModalComponent } from '@shared/modals';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { TaskListModalComponent } from '@app/modules/purchase-order/modals/task-list-modal/task-list-modal.component';
import { SubscriberListModalComponent } from '@app/modules/purchase-order/modals/subscriber-list-modal/subscriber-list-modal.component';
import { Observable, of } from 'rxjs';
import { ShipmentTrackingFormModalComponent } from '@app/modules/po-details/components/shipment-tracking-form-modal/shipment-tracking-form-modal.component';
import { environment } from '@env/environment';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { ExcelService } from '@app/services/excel.service';
import { Store } from '@ngrx/store';

@Component({
  selector: 'ngx-purchase-order-simple',
  templateUrl: './purchase-order-simple.component.html',
  styleUrls: ['./purchase-order-simple.component.scss'],
})
export class PurchaseOrderSimpleComponent implements OnInit {
  isRCDSS = environment.name === 'rcdss';
  purchaseOrder: PurchaseOrderDto = { file_types: [] } as any;
  isDraft: boolean;
  suppliers: Company[];
  purchaseOrderForm: FormGroup;
  @Input() isReducedView: boolean = false;
  @Input() poNumber: string;
  @Input() poCreatedEventEmitter: EventEmitter<PurchaseOrderDto>;
  @ViewChild('supplier_select') supplierSelect;

  @ViewChild('typesInput') typesInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @LocalStorage() organizationId;

  poFiles: FileItem[] = [];
  subscribers: PurchaseOrderSubscriberModel[] = [];
  shipments: Shipment[];
  carriers: any[];
  checklist: any;
  isSaving: boolean;

  canSupplierReceivePos = true;
  isSimpleFlow = true;
  supplierUsers: User[] = [];
  lineItems: LineItem[] = [];
  products: Product[] = [];

  user$: Observable<User>;
  currentUser: User;
  constructor(
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private purchaseOrderService: AppPurchaseOrderService,
    private shipmentService: ShipmentService,
    private $toaster: Toaster,
    private router: Router,
    private $modal: NgbModal,
    private activatedRoute: ActivatedRoute,
    private excelService: ExcelService,
    private store: Store<{ user: User }>,
  ) {}

  async ngOnInit() {
    this.purchaseOrderForm = this.formBuilder.group({
      customer_po_number: [this.poNumber || null, [Validators.required]],
      supplier_id: [null, [Validators.required]],
      rcdss_contract_program: [null],
      order_date: [moment.utc().startOf('d'), [Validators.required]],
      customer_priority: [2, [Validators.required]],
      ship_date: [null],
      cancel_date: [null],
      approver_id: [null, [Validators.required]],
    });
    this.purchaseOrderForm.get('approver_id').setValue('00000000-0000-0000-0000-000000000000');

    if (this.activatedRoute.snapshot.queryParams.draftId) {
      this.isDraft = true;
      this.getPoDrag(this.activatedRoute.snapshot.queryParams.draftId);
    }
    this.suppliers = this.activatedRoute.snapshot.data.suppliers;
    if (!this.suppliers) {
      this.getSuppliers();
    }

    this.purchaseOrderForm
      .get('supplier_id')
      .valueChanges.pipe(filter((value) => !!value))
      .subscribe((value) => {
        const selectedSupplier = this.suppliers.find((p) => p.id === value);
        if (selectedSupplier) {
          this.canSupplierReceivePos = selectedSupplier.can_receive_pos;
        }
        this.getSupplierUsers();
        this.getProductsList();
      });
    this.purchaseOrderForm.get('customer_po_number').setAsyncValidators(this.validatePoNumber.bind(this));
    this.purchaseOrderForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((data) => {
      if (this.purchaseOrderForm.valid) this.savePODraft();
    });

    this.user$ = this.store.select('user');
    this.user$
      .subscribe((user) => {
        this.currentUser = (user as User);
      });
  }

  ngAfterViewInit() {}

  validatePoNumber(control: AbstractControl) {
    if (this.purchaseOrder) {
      if (this.purchaseOrder.customer_po_number === control.value) {
        return of(null);
      }
    }

    return this.companyService
      .isPurchaseOrderNumberAvailable(this.organizationId, control.value)
      .pipe(
        catchError((e) => {
          return of({
            exists: true,
          });
        }),
      )
      .pipe(
        map((result) => {
          if (result) {
            return result;
          }
          return null;
        }),
      );
  }

  getPoDrag(draftId) {
    this.companyService.purchaseOrder(draftId).subscribe((purchaseOrder) => {
      if (moment.utc(purchaseOrder.cancel_date).year() === 1900) {
        delete purchaseOrder.cancel_date;
      }
      if (moment.utc(purchaseOrder.ship_date).year() === 1900) {
        delete purchaseOrder.ship_date;
      }
      this.purchaseOrder = purchaseOrder;
      this.getAdditionalData();
      this.purchaseOrderForm.patchValue(purchaseOrder);
    });
  }

  getAdditionalData() {
    this.companyService.getPoFiles(this.purchaseOrder.id).subscribe((files) => {
      this.poFiles = files;
    });

    if (!this.carriers) {
      this.getShipmentsCarriers();
    }

    this.shipmentService
      .getPurchaseOrderShipments(this.organizationId, this.purchaseOrder.id)
      .subscribe((shipments) => {
        this.shipments = shipments;
      });

    this.purchaseOrderService.getSubscribers(this.purchaseOrder.id).subscribe((subscribers) => {
      this.subscribers = subscribers;
    });

    this.companyService.getCheckList(this.organizationId, this.purchaseOrder.id).subscribe((checklist) => {
      this.checklist = checklist[0];
    });
    this.getProductsList();
  }

  createPO() {
    // if start and cancel dates are the same, lets confirm with the user
    if (moment.utc(this.purchaseOrder.cancel_date).format() === moment.utc(this.purchaseOrder.order_date).format()) {
      if (!window.confirm('Order date and Cancel date are the same. Would you like to proceed anyway?')) {
        return;
      }
    }
    const purchaseOrder = {
      ...this.purchaseOrderForm.getRawValue(),
      id: this.purchaseOrder.id,
      customer_id: this.purchaseOrder.customer_id,
      order_date: this.purchaseOrder.order_date,
      ship_date: this.purchaseOrder.ship_date,
      cancel_date: this.purchaseOrder.cancel_date,
      line_items: this.purchaseOrder.line_items,
      approver_id: this.purchaseOrder.approver_id,
      copy_receivers: this.purchaseOrder.copy_receivers,
      subscribers: this.purchaseOrder.subscribers,
      is_simple: true,
      file_types: this.purchaseOrder.file_types,
    };
    this.isSaving = true;
    this.purchaseOrderService.saveDraft(this.organizationId, purchaseOrder, true).subscribe(
      (po) => {
        if (this.poCreatedEventEmitter) {
          this.poCreatedEventEmitter.emit(po);
          this.$toaster.show('success', 'Success', 'The purchase order was created successfully');
        } else {
          Swal.fire({
            title: 'Congratulations!',
            text: `Your ${this.isRCDSS ? 'vendor' : 'supplier'} has received the ${
              this.isRCDSS
                ? 'Contract.'
                : 'Purchase Order. A notification will be sent to you when it gets approved or rejected.'
            }`,
            icon: 'success',
            showConfirmButton: false,
            timer: 5000,
          });
          setTimeout(() => {
            this.router.navigate(['customers', 'work-commissioned', this.purchaseOrder.id]);
            this.isSaving = false;
          }, 500);
        }
      },
      (error) => {
        this.isSaving = false;
        const errMsg = !!error.error && !!error.error.detail ? error.error.detail : error.message;
        this.$toaster.show('error', this.isRCDSS ? 'Contract' : 'Purchase Order', errMsg);
      },
    );
  }

  navigateToDrafts() {
    if (this.isRCDSS) {
      this.router.navigate(['/customers', 'work-commissioned']);
    } else {
      this.router.navigate(['/customers', 'pos-drafts']);
    }
  }

  updateCheckList(checklist) {
    this.checklist = checklist;
  }

  savePODraft(redirect?) {
    if (this.purchaseOrderForm.invalid) return;
    const poData = this.purchaseOrderForm.getRawValue();
    poData.customer_id = this.organizationId;
    poData.order_date = moment.utc(poData.order_date).format();

    poData.ship_date = poData.ship_date ? moment.utc(poData.ship_date).format() : moment.utc('1900-01-01').format();
    poData.cancel_date = poData.cancel_date
      ? moment.utc(poData.cancel_date).format()
      : moment.utc('1900-01-01').format();
    this.setProductPlaceholder();
    poData.line_items = this.lineItems.map((liteItem: LineItem) => {
      liteItem.order_date = poData.order_date;
      liteItem.ship_date = poData.ship_date;
      liteItem.cancel_date = poData.cancel_date;
      return liteItem;
    });

    const purchaseOrder = {
      ...this.purchaseOrder,
      ...poData,
      is_simple: true,
      // subscribers: this.purchaseOrder?.subscribers,
    };

    this.purchaseOrderService.saveDraft(this.organizationId, purchaseOrder, false, true).subscribe((result) => {
      this.purchaseOrder = result;
      this.getAdditionalData();
      if (redirect) {
        this.navigateToDrafts();
      }
    });

    if (this.isReducedView) {
      this.createPO();
    }
  }

  deletePoDraft() {
    if (!this.purchaseOrder) {
      this.navigateToDrafts();
      return;
    }
    Swal.fire({
      title: 'Confirm Action',
      text: `Do you really want to ${this.isRCDSS ? 'discard this Contract' : 'delete this Purchase Order draft'}?`,
      icon: 'warning',
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: 'btn btn-primary mr-2',
        cancelButton: 'btn btn-danger ml-2',
      },
      confirmButtonColor: '#41b146',
      cancelButtonColor: '#c74141',
    }).then(async (result) => {
      if (result.value) {
        if (this.purchaseOrder && this.purchaseOrder.id) {
          this.purchaseOrderService.deleteDraft(this.organizationId, this.purchaseOrder.id).subscribe(() => {
            this.navigateToDrafts();
          });
        } else {
          this.navigateToDrafts();
        }
        return;
      }
    });
  }

  checklistChanged(checklist) {
    this.checklist = checklist;
  }

  addNewCompany() {
    const modalRef = this.$modal.open(CreateCompanyModalComponent, {
      size: 'lg',
      container: 'nb-layout',
      keyboard: false,
      windowClass: 'company-modal',
    });
    modalRef.componentInstance.company_as = 'supplier';
    modalRef.result
      .then(async (result) => {
        if (result) {
          return this.companyService
            .suppliers(this.organizationId)
            .toPromise()
            .then((suppliers) => {
              this.suppliers = suppliers;
              return result;
            });
        }
        return result;
      })
      .then(
        (result: any) => {
          if (result) {
            this.purchaseOrderForm.get('supplier_id').setValue(result.id);
          }
        },
        (error) => {
          if (error) {
            const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
            this.$toaster.show('error', 'Company', errMsg);
          }
        },
      );
  }

  getSupplierUsers() {
    const supplierId = this.purchaseOrderForm.get('supplier_id').value;
    if (!supplierId) {
      this.supplierUsers = [];
      return;
    }
    this.companyService.users(supplierId).subscribe((users) => {
      this.supplierUsers = users;
    });
  }

  getSuppliers() {
    this.companyService
      .suppliers(this.organizationId)
      .toPromise()
      .then((suppliers) => {
        this.suppliers = suppliers;
      });
  }

  getCheckList() {
    this.companyService.getCheckList(this.organizationId, this.purchaseOrder.id).subscribe((checklist) => {
      this.checklist = checklist[0];
      // this.changeDetectorRef.detectChanges();
    });
  }

  openTaskListModal() {
    const taskModal = this.$modal.open(TaskListModalComponent, {
      container: 'nb-layout',
      keyboard: false,
      ariaLabelledBy: 'modal-task-title',
      size: 'xl',
    });

    taskModal.componentInstance.purchaseOrder = this.purchaseOrder;
    // taskModal.componentInstance.checklist = this.checklist;

    taskModal.result.then((result) => {
      if (result) {
        this.checklist = result;
      }
    });
  }

  addShipmentsModal() {
    const shipmentsModal = this.$modal.open(ShipmentTrackingFormModalComponent, {
      size: 'lg',
      container: 'nb-layout',
    });

    shipmentsModal.componentInstance.companyType = 'customer';
    shipmentsModal.componentInstance.purchaseOrder = this.purchaseOrder;
    shipmentsModal.componentInstance.showFormSwitch = false;

    shipmentsModal.result.then((result) => {
      if (result) {
        this.shipments = [...this.shipments, result];
      }
    });
  }

  openSubscribersModal() {
    const taskModal = this.$modal.open(SubscriberListModalComponent, {
      container: 'nb-layout',
      keyboard: false,
      ariaLabelledBy: 'modal-subscribers-title',
      size: 'xl',
    });

    taskModal.componentInstance.purchaseOrder = this.purchaseOrder;
    taskModal.componentInstance.subscribers = this.subscribers;

    taskModal.result.then((result) => {
      if (result) {
        this.savePODraft();
      }
    });
  }

  getProductsList() {
    if (!this.purchaseOrderForm.get('supplier_id').value) {
      return;
    }

    this.companyService.getProductsByCompanyId(this.purchaseOrderForm.get('supplier_id').value).subscribe(
      (products) => {
        this.products = products;
      },
      (error) => {
        const errMsg = error.error && error.error.detail ? error.error.detail : error.message;
        this.$toaster.show('error', 'Products', errMsg);
      },
    );
  }

  setProductPlaceholder() {
    const placeholderProduct = this.products.find((p) => p.name === 'Placeholder Product');
    if (placeholderProduct) {
      this.addDefaultLineItem(placeholderProduct, 10);
    }
  }

  addDefaultLineItem(selectedProduct, quantityOrdered) {
    const lineItemToBeAdded: LineItem = LineItem.fromProduct(selectedProduct, quantityOrdered);

    if (this.purchaseOrder && this.purchaseOrder.id) {
      lineItemToBeAdded.setPurchaseOrder(this.purchaseOrder);
    }

    if (!lineItemToBeAdded.cancel_date) {
      lineItemToBeAdded.cancel_date = '1900-01-01T00:00:00Z';
    }

    this.lineItems = [lineItemToBeAdded];

    // this.savePODraft();
    return;
  }

  getCarrier(carrierId: string) {
    if (!carrierId || !this.carriers) {
      return {};
    }
    return this.carriers.find((c) => c.id === carrierId);
  }

  getShipmentsCarriers() {
    this.shipmentService.shipmentCarriers(this.organizationId, this.purchaseOrder.id).subscribe((data) => {
      this.carriers = data;
    });
  }  
  
  importExcel(): void {
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
      fileInput.click();
    }
  }
  
  onFileChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.excelService.readExcelFile(file).then((data: any[]) => {
       let excelData = data;  // Store the data for rendering
       excelData.forEach((data) => {
          console.log(data)
          let supplier = this.suppliers.find(x => x.name == data.supplier_name)
          if (supplier) {
            this.savePODraft()
          }
       })
      }).catch(error => {
        console.error('Error reading Excel file:', error);
      });
    }
  }
}
